import { type Canvas, Circle, Rect, Triangle } from "fabric";
import {
  CIRCLE_SHAPE_TYPE,
  RECT_SHAPE_TYPE,
  SHAPE_LAYER_TYPE,
  TRIANGLE_SHAPE_TYPE,
} from "../constants.ts";
import type {
  CanvasSize,
  CircleShapeLayer,
  CircleShapeLayerProperties,
  RectShapeLayer,
  RectShapeLayerExtraSettings,
  ShapeLayer,
  ShapeLayerSettings,
  ShapeType,
  SquareShapeLayerProperties,
  TriangleShapeLayer,
  TriangleShapeLayerProperties,
} from "../types.ts";
import { createLayer, getLayers } from "./layer.ts";
import { updateLayerShadowStyle } from "./shadow.ts";
import { updateLayerStrokeStyle } from "./stroke.ts";

// XXX: We add directly event listener to apply directly the scaling operations to the shape layers, so the scaling property corresponds only to the display scale.
export const addShapeLayerEventListeners = (
  layer: RectShapeLayer | TriangleShapeLayer,
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
) => {
  layer.on({
    modified(this: RectShapeLayer | TriangleShapeLayer) {
      const { width, height, scaleX, scaleY } = this;
      const displayXScale = fabricCanvas.getWidth() / canvasSize.width;
      const displayYScale = fabricCanvas.getHeight() / canvasSize.height;
      this.set({
        width: (width * scaleX) / displayXScale,
        height: (height * scaleY) / displayYScale,
        scaleX: displayXScale,
        scaleY: displayYScale,
      });
    },
  });
};

export const addCircleShapeLayerEventListeners = (
  layer: CircleShapeLayer,
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
) => {
  layer.on({
    modified(this: CircleShapeLayer) {
      const { radius, scaleX } = this;
      const displayXScale = fabricCanvas.getWidth() / canvasSize.width;
      const displayYScale = fabricCanvas.getHeight() / canvasSize.height;
      this.set({
        radius: (radius * scaleX) / displayXScale,
        scaleX: displayXScale,
        scaleY: displayYScale,
      });
    },
  });
};

export const createSquareShapeLayer = ({
  fabricCanvas,
  canvasSize,
  options,
  name,
  positionIndex,
}: {
  fabricCanvas: Canvas;
  canvasSize: CanvasSize;
  name?: string;
  positionIndex?: number;
  options: SquareShapeLayerProperties;
}) => {
  const shapeLayer = new Rect({
    left: options.xPosition ?? 150,
    top: options.yPosition ?? 150,
    width: options.width ?? 100,
    height: options.height ?? 100,
    fill: options.fill ?? "rgba(0, 0, 0, 1)",
    stroke: options.stroke ?? "rgba(0, 0, 0, 1)",
    strokeWidth: options.strokeWidth ?? 0,
    angle: options.rotation ?? 0,
    originX: "center",
    originY: "center",
    rx: options.cornerRadius ?? 0,
    ry: options.cornerRadius ?? 0,
  }) as ShapeLayer;

  shapeLayer.shapeType = RECT_SHAPE_TYPE;
  updateLayerStrokeStyle(shapeLayer, options.strokeStyle ?? "solid");
  updateLayerShadowStyle(
    shapeLayer,
    options.shadowStyle ?? "none",
    options.shadowDirection ?? "top",
    options.shadowDistance ?? "small",
  );

  addShapeLayerEventListeners(
    shapeLayer as RectShapeLayer,
    fabricCanvas,
    canvasSize,
  );

  const currentLayerCount = getLayers(fabricCanvas).length;

  return createLayer({
    fabricCanvas,
    layer: shapeLayer,
    name: name ?? `Shape ${currentLayerCount + 1}`,
    positionIndex: positionIndex ?? currentLayerCount,
    layerType: SHAPE_LAYER_TYPE,
  });
};

export const createCircleShapeLayer = ({
  fabricCanvas,
  canvasSize,
  name,
  positionIndex,
  options,
}: {
  fabricCanvas: Canvas;
  canvasSize: CanvasSize;
  name?: string;
  positionIndex?: number;
  options: CircleShapeLayerProperties;
}) => {
  const shapeLayer = new Circle({
    left: options.xPosition ?? 150,
    top: options.yPosition ?? 150,
    radius: options.radius ?? 100,
    fill: options.fill ?? "rgba(0, 0, 0, 1)",
    stroke: options.stroke ?? "rgba(0, 0, 0, 1)",
    strokeWidth: options.strokeWidth ?? 0,
    angle: options.rotation ?? 0,
    originX: "center",
    originY: "center",
  }) as ShapeLayer;

  shapeLayer.shapeType = CIRCLE_SHAPE_TYPE;
  updateLayerStrokeStyle(shapeLayer, options.strokeStyle ?? "solid");
  updateLayerShadowStyle(
    shapeLayer,
    options.shadowStyle ?? "none",
    options.shadowDirection ?? "top",
    options.shadowDistance ?? "small",
  );

  addCircleShapeLayerEventListeners(
    shapeLayer as CircleShapeLayer,
    fabricCanvas,
    canvasSize,
  );

  const currentLayerCount = getLayers(fabricCanvas).length;

  return createLayer({
    fabricCanvas,
    layer: shapeLayer,
    name: name ?? `Shape ${currentLayerCount + 1}`,
    positionIndex: positionIndex ?? currentLayerCount,
    layerType: SHAPE_LAYER_TYPE,
  });
};

export const createTriangleShapeLayer = ({
  fabricCanvas,
  canvasSize,
  name,
  positionIndex,
  options,
}: {
  fabricCanvas: Canvas;
  canvasSize: CanvasSize;
  name?: string;
  positionIndex?: number;
  options: TriangleShapeLayerProperties;
}) => {
  const shapeLayer = new Triangle({
    left: options.xPosition ?? 150,
    top: options.yPosition ?? 150,
    width: options.width ?? 100,
    height: options.height ?? 100,
    fill: options.fill ?? "rgba(0, 0, 0, 1)",
    stroke: options.stroke ?? "rgba(0, 0, 0, 1)",
    strokeWidth: options.strokeWidth ?? 0,
    angle: options.rotation ?? 0,
    originX: "center",
    originY: "center",
  }) as ShapeLayer;
  shapeLayer.shapeType = TRIANGLE_SHAPE_TYPE;
  updateLayerStrokeStyle(shapeLayer, options.strokeStyle ?? "solid");
  updateLayerShadowStyle(
    shapeLayer,
    options.shadowStyle ?? "none",
    options.shadowDirection ?? "top",
    options.shadowDistance ?? "small",
  );

  addShapeLayerEventListeners(
    shapeLayer as TriangleShapeLayer,
    fabricCanvas,
    canvasSize,
  );

  const currentLayerCount = getLayers(fabricCanvas).length;

  return createLayer({
    fabricCanvas,
    layer: shapeLayer,
    name: name ?? `Shape ${currentLayerCount + 1}`,
    positionIndex: positionIndex ?? currentLayerCount,
    layerType: SHAPE_LAYER_TYPE,
  });
};

export const duplicateShapeLayer = (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  shapeLayer: ShapeLayer,
) => {
  switch (shapeLayer.shapeType) {
    case RECT_SHAPE_TYPE:
      return createSquareShapeLayer({
        fabricCanvas,
        canvasSize,
        options: {
          width: shapeLayer.width,
          height: shapeLayer.height,
          xPosition: shapeLayer.left,
          yPosition: shapeLayer.top,
          fill: shapeLayer.fill,
          stroke: shapeLayer.stroke,
          strokeWidth: shapeLayer.strokeWidth,
          strokeStyle: shapeLayer.strokeStyle,
          rotation: shapeLayer.angle,
          shadowStyle: shapeLayer.shadowStyle,
          shadowDirection: shapeLayer.shadowDirection,
          shadowDistance: shapeLayer.shadowDistance,
          cornerRadius: shapeLayer.rx,
        },
      });
    case CIRCLE_SHAPE_TYPE:
      return createCircleShapeLayer({
        fabricCanvas,
        canvasSize,
        options: {
          radius: shapeLayer.radius,
          xPosition: shapeLayer.left,
          yPosition: shapeLayer.top,
          fill: shapeLayer.fill,
          stroke: shapeLayer.stroke,
          strokeWidth: shapeLayer.strokeWidth,
          strokeStyle: shapeLayer.strokeStyle,
          rotation: shapeLayer.angle,
          shadowStyle: shapeLayer.shadowStyle,
          shadowDirection: shapeLayer.shadowDirection,
          shadowDistance: shapeLayer.shadowDistance,
        },
      });
    case TRIANGLE_SHAPE_TYPE:
      return createTriangleShapeLayer({
        fabricCanvas,
        canvasSize,
        options: {
          width: shapeLayer.width,
          height: shapeLayer.height,
          xPosition: shapeLayer.left,
          yPosition: shapeLayer.top,
          fill: shapeLayer.fill,
          stroke: shapeLayer.stroke,
          strokeWidth: shapeLayer.strokeWidth,
          strokeStyle: shapeLayer.strokeStyle,
          rotation: shapeLayer.angle,
          shadowStyle: shapeLayer.shadowStyle,
          shadowDirection: shapeLayer.shadowDirection,
          shadowDistance: shapeLayer.shadowDistance,
        },
      });
  }
};

export const convertShapeLayer = (
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
  shapeLayer: ShapeLayer,
  newShapeType: ShapeType,
) => {
  if (shapeLayer.shapeType !== newShapeType) {
    const { left, top, fill, stroke, strokeWidth, angle, width, height } =
      shapeLayer;
    const strokeStyle = shapeLayer.strokeStyle;

    const { name, positionIndex } = shapeLayer;
    switch (newShapeType) {
      case RECT_SHAPE_TYPE:
        return createSquareShapeLayer({
          fabricCanvas,
          canvasSize,
          name,
          positionIndex,
          options: {
            width,
            height,
            xPosition: left,
            yPosition: top,
            fill,
            stroke,
            strokeWidth,
            strokeStyle,
            rotation: angle,
          },
        });
      case CIRCLE_SHAPE_TYPE:
        const radius = Math.min(width, height) / 2;
        return createCircleShapeLayer({
          fabricCanvas,
          canvasSize,
          name,
          positionIndex,
          options: {
            radius,
            xPosition: left,
            yPosition: top,
            fill,
            stroke,
            strokeWidth,
            strokeStyle,
            rotation: angle,
          },
        });
      case TRIANGLE_SHAPE_TYPE:
        return createTriangleShapeLayer({
          fabricCanvas,
          canvasSize,
          name,
          positionIndex,
          options: {
            width,
            height,
            xPosition: left,
            yPosition: top,
            fill,
            stroke,
            strokeWidth,
            strokeStyle,
            rotation: angle,
          },
        });
    }
  }
};

export const updateShapeLayer = (
  shapeLayer: ShapeLayer,
  options: Partial<Required<Omit<ShapeLayerSettings, "shapeType">>>,
) => {
  shapeLayer.canvas?.fire("layer:shape:changed", {
    layer: shapeLayer,
    changes: options,
  });
  shapeLayer.set({
    fill: options.fill ?? shapeLayer.fill,
    angle: options.rotation ?? shapeLayer.angle,
    stroke: options.stroke ?? shapeLayer.stroke,
    strokeWidth: options.strokeWidth ?? shapeLayer.strokeWidth,
  });
  if (options.strokeStyle !== undefined) {
    updateLayerStrokeStyle(shapeLayer, options.strokeStyle);
  }
  if (
    options.shadowStyle !== undefined ||
    options.shadowDirection !== undefined ||
    options.shadowDistance !== undefined
  ) {
    updateLayerShadowStyle(
      shapeLayer,
      options.shadowStyle ?? shapeLayer.shadowStyle,
      options.shadowDirection ?? shapeLayer.shadowDirection,
      options.shadowDistance ?? shapeLayer.shadowDistance,
    );
  }
  shapeLayer.setCoords();
};

export const updateRectShapeLayer = (
  rectShapeLayer: RectShapeLayer,
  options: RectShapeLayerExtraSettings,
) => {
  rectShapeLayer.canvas?.fire("layer:rect:changed", {
    layer: rectShapeLayer,
    changes: options,
  });
  rectShapeLayer.set({
    rx: options.cornerRadius ?? rectShapeLayer.rx,
    ry: options.cornerRadius ?? rectShapeLayer.ry,
  });
  rectShapeLayer.setCoords();
};
